import { GeckoMeasurement } from './geckoMeasurement'
import { GeckoMeasurementUnit } from './geckoMeasurementUnit'

export type GeckoMeasurementRangeSerializable = {
  start: number
  end: number
  unit: string
}

/**
 * A utility class representing a range of measurements, used most frequently
 * to describe range of "acceptable" measurement to filter by.
 */
export class GeckoMeasurementRange {
  //----------------------------------------------------------------------------

  /** The start of this value range */
  public readonly start: GeckoMeasurement

  /** The end of this value range */
  public readonly end: GeckoMeasurement

  /** The size (a.k.a. width) of this range */
  public readonly size: GeckoMeasurement

  /** The difference between the end and start of a measurement range */
  public readonly delta: GeckoMeasurement

  /**
   * A simple constructor- private to encourage use of more descriptive static
   * factory methods like CreateFromValues()
   * @param start     The start value
   * @param end       The end value
   * @private
   */
  private constructor(start: GeckoMeasurement, end: GeckoMeasurement) {
    console.assert(
      start.unit === end.unit,
      'Value range min and max must share the same measurement unit'
    )
    this.start = start
    this.end = end
    this.delta = end.minus(start)
    this.size = this.delta.abs()
  }

  /** The minimum value in this range */
  public get min(): GeckoMeasurement {
    return this.start.num < this.end.num ? this.start : this.end
  }

  /** The maximum value in this range */
  public get max(): GeckoMeasurement {
    return this.start.num < this.end.num ? this.end : this.start
  }

  /**
   * An accessor method for this value range's measurement unit (an accessor
   * rather than a variable since this is already stored on both min and max)
   */
  public get unit(): GeckoMeasurementUnit {
    return this.min.unit
  }

  //----------------------------------------------------------------------------

  /**
   * A method for checking whether two ranges are exactly equal, not just
   * equivalent (i.e. (0 - 0 in) !== (0 - 0 ft))
   * @param a               The first range object, or undefined
   * @param b               The second range object, or undefined
   * @constructor
   */
  public static Equals(
    a: GeckoMeasurementRange | undefined,
    b: GeckoMeasurementRange | undefined
  ): boolean {
    if (a === b) return true
    if (!a) return !b
    if (!b) return !a
    return (
      GeckoMeasurement.Equals(a.start, b.start) &&
      GeckoMeasurement.Equals(a.end, b.end)
    )
  }

  /**
   * Factory method for creating a value range object from two GeckoValues
   * @param start       The start of the value range
   * @param end         The end of the value range
   * @constructor
   */
  public static CreateFromStartEnd(
    start: GeckoMeasurement,
    end: GeckoMeasurement
  ): GeckoMeasurementRange {
    return new GeckoMeasurementRange(start, end.convertTo(start.unit))
  }

  /**
   * Factory method for creating a value range object from two measurement
   * @param start     The start value
   * @param end       The end value
   * @param unit      The measurement unit of these measurements
   * @constructor
   */
  public static CreateFromValues(
    start: number,
    end: number,
    unit: GeckoMeasurementUnit
  ): GeckoMeasurementRange {
    return new GeckoMeasurementRange(
      GeckoMeasurement.CreateFromValue(start, unit),
      GeckoMeasurement.CreateFromValue(end, unit)
    )
  }

  public static CreateFromSerializable(
    serialized: GeckoMeasurementRangeSerializable
  ): GeckoMeasurementRange {
    const unit = GeckoMeasurementUnit.GetOrCreate(serialized.unit)
    return GeckoMeasurementRange.CreateFromValues(
      serialized.start,
      serialized.end,
      unit
    )
  }

  public static Serialize(
    gmr: GeckoMeasurementRange
  ): GeckoMeasurementRangeSerializable {
    return {
      start: gmr.start.num,
      end: gmr.end.num,
      unit: gmr.unit.id,
    }
  }

  //----------------------------------------------------------------------------

  /**
   * A method which returns a value range object with new measurement units.
   * Note that if no conversion is necessary, this instance will be returned.
   * @param newUnit         The new measurement unit of this value range
   */
  public convertTo(
    newUnit: GeckoMeasurementUnit | undefined
  ): GeckoMeasurementRange {
    if (this.unit === newUnit || !newUnit) return this

    if (newUnit === GeckoMeasurementUnit.Frames) {
      throw new Error(
        "Convert to frames using convertToFrames instead, using the asset model's frame size"
      )
    }

    return GeckoMeasurementRange.CreateFromStartEnd(
      this.min.convertTo(newUnit),
      this.max.convertTo(newUnit)
    )
  }

  public convertToFrames(frameSize: GeckoMeasurement): GeckoMeasurementRange {
    const rangeInFrameMeasurement = this.convertTo(frameSize.unit)
    return GeckoMeasurementRange.CreateFromStartEnd(
      GeckoMeasurement.CreateFromValue(
        rangeInFrameMeasurement.min.num / frameSize.num,
        GeckoMeasurementUnit.Frames
      ),
      GeckoMeasurement.CreateFromValue(
        rangeInFrameMeasurement.max.num / frameSize.num,
        GeckoMeasurementUnit.Frames
      )
    )
  }

  /**
   * A method returning a legible string form of this object's data
   */
  public toString(): string {
    return `${this.min.num} - ${this.max.num} ${this.unit.id}`
  }
}
